import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";
import { Icon } from "../../Component";
import YearMonthPicker from "./YearMonthPicker";

const DateRangePicker = ({ onDateChange, moveDate, urlDates, isMonthPicker = false }) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedDate, setSelectedDate] = useState("");

  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // useEffect(() => {
  //   setSelectedDate(new Date());
  // }, []);

  useEffect(() => {
    if (moveDate) {
      setStartDate(moveDate.startDate);
      setEndDate(moveDate.endDate);
      const moveDateObj = new Date(moveDate.startDate);
      if (isMonthPicker) {
        setSelectedDate(new Date(moveDateObj.getFullYear(), moveDateObj.getMonth(), 1));
      } else {
        setSelectedDate(moveDateObj);
      }
    } else if (urlDates) {
      setStartDate(urlDates.startDate);
      setEndDate(urlDates.endDate);
      const urlDateObj = new Date(urlDates.startDate);
      if (isMonthPicker) {
        setSelectedDate(new Date(urlDateObj.getFullYear(), urlDateObj.getMonth(), 1));
      } else {
        setSelectedDate(urlDateObj);
      }
    } else {
      if (isMonthPicker) {
        const now = new Date();
        setSelectedDate(new Date(now.getFullYear(), now.getMonth(), 1));
      } else {
        setSelectedDate(new Date());
      }
    }
  }, [moveDate, urlDates, isMonthPicker]);

  // Month picker handler
  const handleMonthDateChange = useCallback(
    (selectedDate) => {
      const currentDate = new Date(selectedDate);
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();

      // Get first day of the month
      const startOfMonth = new Date(year, month, 1);
      // Get last day of the month
      const endOfMonth = new Date(year, month + 1, 0);

      // Format dates in local timezone
      const formattedStartDate = formatDateLocal(startOfMonth);
      const formattedEndDate = formatDateLocal(endOfMonth);

      // Update the state with the selected dates
      setStartDate(formattedStartDate);
      setEndDate(formattedEndDate);

      // Pass the selected dates to the parent component
      onDateChange(formattedStartDate, formattedEndDate);
    },
    [onDateChange],
  );

  // Week picker handler (original logic)
  const handleWeekDateChange = useCallback(
    (selectedDate) => {
      const currentDate = new Date(selectedDate);
      const currentDay = currentDate.getDay();
      const daysToSubtract = currentDay === 0 ? 6 : currentDay - 1; // Adjust to the previous Monday

      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - daysToSubtract);

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6); // End of the week (Sunday)

      // Update the state with the selected dates
      setStartDate(startOfWeek.toISOString().slice(0, 10));
      setEndDate(endOfWeek.toISOString().slice(0, 10));

      // Pass the selected dates to the parent component
      onDateChange(startOfWeek.toISOString().slice(0, 10), endOfWeek.toISOString().slice(0, 10));
    },
    [onDateChange],
  );

  const handleDateChange = isMonthPicker ? handleMonthDateChange : handleWeekDateChange;

  useEffect(() => {
    if (!moveDate && !urlDates && selectedDate) {
      handleDateChange(selectedDate);
    }
  }, [selectedDate, moveDate, urlDates, handleDateChange]);

  // useEffect(() => {
  //   selectedDate && handleDateChange(selectedDate);
  // }, [selectedDate]);

  // Month picker update function
  const updateMonthRange = (selectedMonth) => {
    const currentDate = new Date(selectedMonth);
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // Get first day of the month
    const startOfMonth = new Date(year, month, 1);
    // Get last day of the month
    const endOfMonth = new Date(year, month + 1, 0);

    // Format dates in local timezone
    const formattedStartDate = formatDateLocal(startOfMonth);
    const formattedEndDate = formatDateLocal(endOfMonth);

    setStartDate(formattedStartDate);
    setEndDate(formattedEndDate);
    onDateChange(formattedStartDate, formattedEndDate);
    setSelectedDate(selectedMonth);
  };

  // Week picker update function (original logic)
  const updateWeekRange = (newStartDate) => {
    const newEndDate = new Date(newStartDate.getTime() + 6 * 24 * 60 * 60 * 1000);
    setStartDate(newStartDate.toISOString().slice(0, 10));
    setEndDate(newEndDate.toISOString().slice(0, 10));
    onDateChange(newStartDate.toISOString().slice(0, 10), newEndDate.toISOString().slice(0, 10));
    setSelectedDate(newStartDate);
  };

  const updateDateRange = isMonthPicker ? updateMonthRange : updateWeekRange;

  // Month picker navigation
  const handleMonthNextPrev = (isNext) => {
    const currentDate = startDate ? new Date(startDate) : new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // Move to next or previous month
    const newMonth = isNext ? month + 1 : month - 1;
    const newDate = new Date(year, newMonth, 1);
    updateDateRange(newDate);
  };

  // Week picker navigation (original logic)
  const handleWeekNextPrev = (isNext) => {
    const currentDate = startDate ? new Date(startDate) : new Date();
    const daysToAdd = isNext ? 7 : -7;
    const newStartDate = new Date(currentDate.getTime() + daysToAdd * 24 * 60 * 60 * 1000);
    const startDayOfWeek = newStartDate.getDay();
    const daysToSubtract = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;
    newStartDate.setDate(newStartDate.getDate() - daysToSubtract);
    updateDateRange(newStartDate);
  };

  const handleNextPrev = isMonthPicker ? handleMonthNextPrev : handleWeekNextPrev;

  return (
    <div className="input-group ">
      <span
        className="input-group-text bg-purple text-white border-0 cursor-pointer"
        onClick={() => handleNextPrev(false)}
      >
        <Icon className="fs-4" name="chevron-left" />
      </span>
      <YearMonthPicker
        onChange={(date) => {
          handleDateChange(date);
          setSelectedDate(date);
        }}
        placeholder={
          isMonthPicker
            ? startDate && endDate
              ? moment(startDate).format("MMMM YYYY")
              : "Select Month"
            : startDate && endDate
              ? `${moment(startDate).format("MMM DD")} - ${moment(endDate).format("MMM DD")}`
              : "Select Date"
        }
        selected={selectedDate}
        showMonthYearPicker={isMonthPicker}
      />
      <span
        className="input-group-text bg-purple text-white border-0 cursor-pointer"
        onClick={() => handleNextPrev(true)}
      >
        <Icon className="fs-4" name="chevron-right" />
      </span>
    </div>
  );
};

export default DateRangePicker;
